// Camera scanner modal, a kit Dialog.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert } from '../ui/Alert';
import { buttonClass } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import Select from '../ui/Select';
import Tooltip from '../ui/Tooltip';
import { useBarcodeCamera } from './useBarcodeCamera';
import { enumerateBackCameras, pickBestCameraId, setTorch, trackSupportsTorch } from './camera-utils';
import type { BarcodeFormat, ScanResult } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  onScan: (result: ScanResult) => void;
  formats?: BarcodeFormat[];
  title?: string;
  hint?: string;
}

const TorchIcon: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10" />
  </svg>
);

const CornerBrackets: React.FC = () => (
  <div className="absolute inset-6 pointer-events-none">
    <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/80 rounded-tl-md" />
    <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-primary/80 rounded-tr-md" />
    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-primary/80 rounded-bl-md" />
    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary/80 rounded-br-md" />
  </div>
);

const BarcodeScannerModal: React.FC<Props> = ({
  open,
  onClose,
  onScan,
  formats,
  title = 'Scan Code',
  hint = 'Point camera at a code',
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [torchSupported, setTorchSupportedState] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Initialize camera list when modal opens. Guards against setState after
  // close mid-init via the mounted flag.
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setPermissionError(null);
    setTorchSupportedState(false);
    setTorchOn(false);

    (async () => {
      try {
        const cams = await enumerateBackCameras();
        if (!mounted) return;
        setCameras(cams);
        const best = await pickBestCameraId();
        if (!mounted) return;
        setActiveId(best ?? cams[0]?.deviceId ?? null);
      } catch (err) {
        const e = err as Error & { name?: string };
        if (!mounted) return;
        if (e.name === 'NotAllowedError' || e.name === 'SecurityError') {
          setPermissionError('Camera permission denied. Allow camera access in your browser settings.');
        } else if (e.name === 'NotFoundError' || e.name === 'OverconstrainedError') {
          setPermissionError('No usable camera found on this device.');
        } else {
          setPermissionError(e.message || 'Failed to access camera.');
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open]);

  // Detect torch capability whenever active stream changes.
  useEffect(() => {
    if (!open || !activeId) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    const check = () => {
      if (cancelled) return;
      const stream = video.srcObject as MediaStream | null;
      const track = stream?.getVideoTracks()[0];
      setTorchSupportedState(trackSupportsTorch(track));
    };

    video.addEventListener('playing', check);
    return () => {
      cancelled = true;
      video.removeEventListener('playing', check);
    };
  }, [open, activeId]);

  const toggleTorch = async () => {
    const video = videoRef.current;
    const stream = video?.srcObject as MediaStream | null;
    const track = stream?.getVideoTracks()[0];
    if (!track) return;
    const ok = await setTorch(track, !torchOn);
    if (ok) setTorchOn(!torchOn);
  };

  const handleResult = useCallback(
    (result: ScanResult) => {
      if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(40);
      }
      onScan(result);
      onClose();
    },
    [onScan, onClose],
  );

  useBarcodeCamera({
    videoRef,
    deviceId: activeId,
    formats,
    onResult: handleResult,
    active: open && !!activeId && !permissionError,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      meta={permissionError ? 'Camera access required' : hint}
      size="md"
      footer={
        <button type="button" onClick={onClose} className={buttonClass('quiet', 'md')}>
          Cancel
        </button>
      }
    >
      <div className="space-y-4">
        {permissionError ? (
          <Alert tone="error">
            {permissionError}
            <span className="mt-1 block text-xs text-on-surface-variant">
              You can still type the code manually after closing this dialog.
            </span>
          </Alert>
        ) : (
          <>
            <div className="relative bg-black rounded-md overflow-hidden aspect-square border border-gray-700/40">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                autoPlay
                playsInline
                muted
              />
              <CornerBrackets />
              {torchSupported && (
                <Tooltip label={torchOn ? 'Turn torch off' : 'Turn torch on'}>
                  <button
                    type="button"
                    onClick={toggleTorch}
                    className={`absolute bottom-2 right-2 p-2 rounded-full border transition-all duration-150 ${
                      torchOn
                        ? 'bg-primary text-black border-primary shadow-[0_0_12px_rgba(255,204,51,0.4)]'
                        : 'bg-black/60 hover:bg-black/80 text-white border-gray-700/60'
                    }`}
                    aria-label={torchOn ? 'Turn torch off' : 'Turn torch on'}
                  >
                    <TorchIcon />
                  </button>
                </Tooltip>
              )}
            </div>

            {cameras.length > 1 && (
              <div>
                <label htmlFor="barcode-camera" className="block text-xs text-on-surface-variant uppercase tracking-wide mb-1">Camera</label>
                <Select
                  id="barcode-camera"
                  label="Camera"
                  value={activeId ?? ''}
                  onChange={setActiveId}
                  options={cameras.map((c) => ({ key: c.deviceId, label: c.label || `Camera ${c.deviceId.slice(0, 6)}` }))}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};

export default BarcodeScannerModal;
